import { INPUT_TYPES } from '../comicForm/constants/inputTypes';

const DRAFT_KEY = 'scicomic-landing-draft';
const DB_NAME = 'scicomic-drafts';
const STORE_NAME = 'landing-files';

export interface LandingDraft {
  inputType: string;
  description: string;
  file: File | null;
  comicLanguage?: string;
}

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const writeFile = async (file: File | null) => {
  if (!('indexedDB' in window)) return;

  const database = await openDatabase();
  const transaction = database.transaction(STORE_NAME, 'readwrite');
  const store = transaction.objectStore(STORE_NAME);

  if (file) {
    store.put(file, DRAFT_KEY);
  } else {
    store.delete(DRAFT_KEY);
  }

  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
};

const readAndDeleteFile = async () => {
  if (!('indexedDB' in window)) return null;

  const database = await openDatabase();
  const transaction = database.transaction(STORE_NAME, 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  const request = store.get(DRAFT_KEY);
  const file = await new Promise<File | null>((resolve, reject) => {
    request.onsuccess = () => resolve((request.result as File) || null);
    request.onerror = () => reject(request.error);
  });

  store.delete(DRAFT_KEY);
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
  return file;
};

export const saveLandingDraft = async (draft: LandingDraft) => {
  sessionStorage.setItem(
    DRAFT_KEY,
    JSON.stringify({
      inputType: draft.inputType,
      description: draft.description,
      comicLanguage: draft.comicLanguage,
    }),
  );
  await writeFile(draft.file);
};

export const consumeLandingDraft = async (): Promise<LandingDraft | null> => {
  const storedDraft = sessionStorage.getItem(DRAFT_KEY);
  if (!storedDraft) return null;

  sessionStorage.removeItem(DRAFT_KEY);
  const draft = JSON.parse(storedDraft) as Omit<LandingDraft, 'file'>;
  const file =
    draft.inputType === INPUT_TYPES.upload ? await readAndDeleteFile() : null;

  return { ...draft, file };
};
